import React, { useState } from "react";
import axios from "axios";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { SERVICES } from "@/lib/business";
import { Loader2, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuoteDialog({ open, onOpenChange, defaultService }) {
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        service: defaultService || "",
        property_type: "",
        message: "",
    });

    React.useEffect(() => {
        if (defaultService) setForm((f) => ({ ...f, service: defaultService }));
    }, [defaultService]);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.service) {
            toast.error("Please fill out name, email, phone and service.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API}/quote`, form);
            toast.success(res.data?.message || "Quote request sent!");
            onOpenChange(false);
            setForm({
                name: "",
                email: "",
                phone: "",
                service: defaultService || "",
                property_type: "",
                message: "",
            });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "Could not submit quote. Please try again or call us directly."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="quote-dialog"
                className="max-w-xl bg-white p-0 overflow-hidden"
            >
                <div className="bg-navy text-white px-6 py-5">
                    <DialogHeader>
                        <DialogTitle className="text-white text-2xl font-display flex items-center gap-2">
                            <ShieldCheck className="h-5 w-5 text-gold" />
                            Get Your Free Quote
                        </DialogTitle>
                        <DialogDescription className="text-white/70">
                            Tell us a bit about your project — we'll respond within 1 business day.
                        </DialogDescription>
                    </DialogHeader>
                </div>
                <form onSubmit={onSubmit} className="p-6 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="q-name" className="text-navy">Full name *</Label>
                            <Input
                                id="q-name"
                                data-testid="quote-name-input"
                                value={form.name}
                                onChange={(e) => update("name", e.target.value)}
                                required
                                className="mt-1.5"
                            />
                        </div>
                        <div>
                            <Label htmlFor="q-phone" className="text-navy">Phone *</Label>
                            <Input
                                id="q-phone"
                                data-testid="quote-phone-input"
                                value={form.phone}
                                onChange={(e) => update("phone", e.target.value)}
                                required
                                className="mt-1.5"
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="q-email" className="text-navy">Email *</Label>
                        <Input
                            id="q-email"
                            data-testid="quote-email-input"
                            type="email"
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            required
                            className="mt-1.5"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="q-service" className="text-navy">Service *</Label>
                            <Select
                                value={form.service}
                                onValueChange={(v) => update("service", v)}
                            >
                                <SelectTrigger
                                    id="q-service"
                                    data-testid="quote-service-select"
                                    className="mt-1.5"
                                >
                                    <SelectValue placeholder="Select a service" />
                                </SelectTrigger>
                                <SelectContent>
                                    {SERVICES.map((s) => (
                                        <SelectItem
                                            key={s.key}
                                            value={s.label}
                                            data-testid={`quote-service-option-${s.key}`}
                                        >
                                            {s.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="q-prop" className="text-navy">Property type</Label>
                            <Select
                                value={form.property_type}
                                onValueChange={(v) => update("property_type", v)}
                            >
                                <SelectTrigger
                                    id="q-prop"
                                    data-testid="quote-property-select"
                                    className="mt-1.5"
                                >
                                    <SelectValue placeholder="Residential / Commercial" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Residential">Residential</SelectItem>
                                    <SelectItem value="Commercial">Commercial</SelectItem>
                                    <SelectItem value="Both">Both</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="q-msg" className="text-navy">Project details</Label>
                        <Textarea
                            id="q-msg"
                            data-testid="quote-message-input"
                            value={form.message}
                            onChange={(e) => update("message", e.target.value)}
                            placeholder="Number of windows, building type, timeline..."
                            rows={4}
                            className="mt-1.5"
                        />
                    </div>
                    <button
                        data-testid="quote-submit-button"
                        type="submit"
                        disabled={loading}
                        className="btn-gold w-full disabled:opacity-70"
                    >
                        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                        {loading ? "Sending..." : "Request My Free Quote"}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
